import {forwardRef, Ref} from 'react';

import styled from 'styled-components';

import {
  ActionTypeExternalComponent,
  ActionTypeOnClickComponent,
  TopLevelSpan,
  ActionTypeRouterComponent
} from 'components/design-guide/Action/ActionTypeComponents';
import {ActionStyleButtonComponent} from 'components/design-guide/Action/Button';
import ActionLoadingSpinner, {RightSidePadding} from 'components/design-guide/Action/Button/LoadingSpinner';
import {
  ActionStyleTextComponent,
  ActionStyleIconComponent,
  ActionStyleImageComponent,
  ActionStyleStyledIconComponent
} from 'components/design-guide/Action/Hyperlink';
import {ActionProps} from 'components/design-guide/Action/types';
import {ActionStyle, ActionType} from 'components/design-guide/enums';
import {useGenerateNameForDataID} from 'utils/adobe-target/abtest-prefetch';

const Action = forwardRef((props: ActionProps, ref: Ref<any>) => {
  if (
    props.disabled &&
    props.actionType !== ActionType.ONCLICK &&
    [ActionStyle.TEXT, ActionStyle.INLINE_TEXT, ActionStyle.ICON].includes(props.actionStyle)
  ) {
    return null;
  }
  return (
    <TopLevelSpan {...props} {...{'data-testid': undefined}}>
      <ActionTypeComponent ref={ref} {...props} />
    </TopLevelSpan>
  );
});

const ActionTypeComponent = forwardRef((props: ActionProps, ref: Ref<any>) => {
  const {actionType, typeProps} = props;
  const name = useGenerateNameForDataID({
    name: props.name,
    abTestMBox: props.reportToTargetOnClick?.analyticsExperienceMBox
  });
  switch (actionType) {
    case ActionType.ROUTER:
      return (
        <ActionTypeRouterComponent
          ref={ref}
          name={name}
          $actionProps={props}
          to={typeProps.to}
          replace={typeProps.replace}>
          <ActionStyleComponent {...props} />
        </ActionTypeRouterComponent>
      );

    case ActionType.EXTERNAL:
      return (
        <ActionTypeExternalComponent ref={ref} {...props} {...typeProps} name={name}>
          <ActionStyleComponent {...props} />
        </ActionTypeExternalComponent>
      );

    case ActionType.NEWTAB:
      return (
        <ActionTypeExternalComponent
          ref={ref}
          {...props}
          {...typeProps}
          name={name}
          target={'_blank'}
          rel="noopener noreferrer">
          <ActionStyleComponent {...props} />
        </ActionTypeExternalComponent>
      );

    case ActionType.PHONE:
      return (
        <ActionTypeExternalComponent ref={ref} {...props} name={name} href={`tel:${typeProps.phoneNumber}`}>
          <ActionStyleComponent {...props} />
        </ActionTypeExternalComponent>
      );

    case ActionType.EMAIL:
      return (
        <ActionTypeExternalComponent ref={ref} {...props} name={name} href={`mailto:${typeProps.emailAddress}`}>
          <ActionStyleComponent {...props} />
        </ActionTypeExternalComponent>
      );

    case ActionType.ONCLICK:
      return (
        <ActionTypeOnClickComponent ref={ref} {...props} name={name}>
          <ActionStyleComponent {...props} />
        </ActionTypeOnClickComponent>
      );

    default:
      return <></>;
  }
});

const ActionStyleComponent = (props: ActionProps) => {
  const {actionStyle, styleProps, disabled, selected} = props;
  switch (actionStyle) {
    case ActionStyle.TEXT:
    case ActionStyle.INLINE_TEXT:
      return (
        <ActionStyleTextComponent {...{actionStyle, styleProps, disabled}}>{props.children}</ActionStyleTextComponent>
      );

    case ActionStyle.ICON:
      return (
        <ActionStyleIconComponent {...{actionStyle, styleProps, disabled}}>{props.children}</ActionStyleIconComponent>
      );

    case ActionStyle.STYLED_ICON:
      return (
        <ActionStyleStyledIconComponent {...{actionStyle, styleProps, disabled}}>
          {props.children}
        </ActionStyleStyledIconComponent>
      );

    case ActionStyle.BUTTON:
      return (
        <ActionStyleButtonComponent
          disabled={disabled}
          selected={selected}
          justifyContent={'center'}
          alignItems={'center'}
          loadingSpinner={styleProps.withSpinner}
          buttonStyle={styleProps.buttonStyle}>
          {styleProps.withSpinner && (
            <ActionLoadingSpinner
              includeButtonPadding
              buttonStyle={styleProps.buttonStyle}
              active={styleProps.withSpinner.active || false}
            />
          )}
          {props.children}
          {styleProps.withSpinner && <RightSidePadding />}
        </ActionStyleButtonComponent>
      );

    case ActionStyle.IMAGE:
      return <ActionStyleImageComponent {...{actionStyle, styleProps, disabled}} />;
    default:
      return <></>;
  }
};

//exporting it as a styled component allows it to be targeted by styled-components selectors
export default styled(Action)``;
